/**
 * Shared HTTP client (ARCHITECTURE §7, `@flozic/shared` `api/`).
 *
 * A thin fetch wrapper used by BOTH the dashboard and the widget:
 * - prepends a configurable base URL,
 * - injects the auth token (via an async provider so it works for both the
 *   app-JWT owner flow and the app-JWT agent flow, and for the widget's opaque
 *   session token),
 * - transparently refreshes on 401 and retries once,
 * - normalizes errors into a single {@link ApiError} shape.
 *
 * Skeleton-grade: the fetch plumbing is real, but auth wiring is left to the
 * apps via {@link HttpClientOptions}. Business endpoints are typed in `resources/`.
 */

/**
 * Window event dispatched when the backend denies an action with code `plan_limit` (HTTP 402).
 * detail: `{ message: string }`. The dashboard's UpgradePlanModal listens for this.
 */
export const PLAN_LIMIT_EVENT = 'lc:plan-limit';

/** Normalized API error surfaced to callers regardless of transport failure. */
export class ApiError extends Error {
  /** HTTP status (0 for network/transport failures). */
  readonly status: number;
  /** Machine-readable code from the backend error envelope, if any. */
  readonly code: string | null;
  /** Raw parsed body, when available. */
  readonly body: unknown;

  constructor(message: string, status: number, code: string | null, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

/** Supported request methods. */
export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

/** Per-request options. */
export interface RequestOptions {
  method?: HttpMethod;
  /** JSON body; serialized automatically. Use `formData` for multipart. */
  body?: unknown;
  formData?: FormData;
  /** Array values are serialized as repeated keys (`?status=a&status=b`). */
  query?: Record<
    string,
    string | number | boolean | undefined | null | ReadonlyArray<string | number | boolean>
  >;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Skip auth token injection (e.g. public widget calls). */
  anonymous?: boolean;
}

/** Client configuration + auth hooks provided by the host app. */
export interface HttpClientOptions {
  /** e.g. `import.meta.env.VITE_API_BASE_URL`. */
  baseUrl: string;
  /** Returns the current access token (or null). May be async. */
  getToken?: () => string | null | Promise<string | null>;
  /**
   * Called on 401 to obtain a fresh token. Return the new token to retry once,
   * or null to give up (caller sees the 401). Should be idempotent/deduped by
   * the app's token store.
   */
  refreshToken?: () => Promise<string | null>;
  /** Extra headers applied to every request (e.g. widget public key). */
  defaultHeaders?: Record<string, string>;
}

/**
 * Serialize a query object to a `?a=b&c=d` string (skips null/undefined).
 * Array values become repeated keys (`?status=a&status=b`) — the FastAPI
 * convention for `list[...]` query params.
 */
function buildQuery(query: RequestOptions['query']): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item));
    } else {
      params.append(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * The shared HTTP client. Construct once per app with the base URL + auth hooks
 * and reuse for all resources.
 */
export class HttpClient {
  private readonly opts: HttpClientOptions;

  constructor(opts: HttpClientOptions) {
    this.opts = opts;
  }

  /** Typed request. Resolves the parsed JSON body or throws {@link ApiError}. */
  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const token = options.anonymous ? null : await this.resolveToken();
    const res = await this.rawFetch(path, options, token);

    // On 401, attempt a single refresh + retry (ARCHITECTURE §7 token refresh).
    if (res.status === 401 && !options.anonymous && this.opts.refreshToken) {
      const refreshed = await this.opts.refreshToken();
      if (refreshed) {
        const retry = await this.rawFetch(path, options, refreshed);
        return this.parse<T>(retry);
      }
    }
    return this.parse<T>(res);
  }

  get<T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(path, { ...options, method: 'GET' });
  }

  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'POST', body });
  }

  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PATCH', body });
  }

  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PUT', body });
  }

  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'DELETE' });
  }

  private async resolveToken(): Promise<string | null> {
    if (!this.opts.getToken) return null;
    return (await this.opts.getToken()) ?? null;
  }

  /** Perform the underlying fetch with headers/body assembled. */
  private async rawFetch(
    path: string,
    options: RequestOptions,
    token: string | null,
  ): Promise<Response> {
    const url = `${this.opts.baseUrl}${path}${buildQuery(options.query)}`;
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...this.opts.defaultHeaders,
      ...options.headers,
    };
    if (token) headers.Authorization = `Bearer ${token}`;

    let body: BodyInit | undefined;
    if (options.formData) {
      body = options.formData; // browser sets multipart boundary
    } else if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(options.body);
    }

    try {
      return await fetch(url, {
        method: options.method ?? 'GET',
        headers,
        body,
        signal: options.signal,
      });
    } catch (cause) {
      // Network/transport failure — normalize to ApiError(status 0).
      throw new ApiError(
        cause instanceof Error ? cause.message : 'Network request failed',
        0,
        'network_error',
        cause,
      );
    }
  }

  /** Parse a response into T or throw a normalized {@link ApiError}. */
  private async parse<T>(res: Response): Promise<T> {
    if (res.status === 204) return undefined as T;

    const text = await res.text();
    const parsed: unknown = text ? safeJsonParse(text) : null;

    if (!res.ok) {
      // Backend error envelope (core/errors.py) is {"detail": {"code", "message"}}. FastAPI's own
      // request-validation errors use {"detail": [...]} or {"detail": "<string>"}. Extract from
      // detail first (that's where domain errors live), then top-level, then the status text.
      const detail = isRecord(parsed) ? parsed.detail : undefined;
      const code =
        isRecord(detail) && typeof detail.code === 'string'
          ? detail.code
          : isRecord(parsed) && typeof parsed.code === 'string'
            ? parsed.code
            : null;
      const message =
        isRecord(detail) && typeof detail.message === 'string'
          ? detail.message
          : typeof detail === 'string'
            ? detail
            : isRecord(parsed) && typeof parsed.message === 'string'
              ? parsed.message
              : res.statusText || `HTTP ${res.status}`;
      if (code === 'plan_limit' && typeof window !== 'undefined') {
        // Announce plan-limit denials globally so the dashboard can open its upgrade-plan popup
        // from ANY call site. The widget bundle shares this client but registers no listener, so
        // visitors never see an upgrade prompt.
        window.dispatchEvent(new CustomEvent(PLAN_LIMIT_EVENT, { detail: { message } }));
      }
      throw new ApiError(message, res.status, code, parsed);
    }
    return parsed as T;
  }
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
